/* eslint-disable no-empty */
/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { Formik } from 'formik';
import FileUpload from '../FileUpload';
import { useAttachmentsContext } from '@/context/attachmentsContext';
import { FileExistsDialog } from '../dialogs/FileExistsDialog';
import { FolderSelectDialog } from '../dialogs/FolderSelectDialog';
import { useSharepointUploadConflicts } from '@/hooks/useSharepointUploadConflicts';

const getRootFolderId = ({ deal, client, relationshipCompany }) =>
	deal?.driveId ?? relationshipCompany?.driveId ?? client?.clientRow?.driveId ?? client?.driveId;

export const FileUploadMSGraph = () => {
	const {
		deal,
		client,
		relationshipCompany,
		canCreate,
		handleUploadFiles,
		clientFolderDriveId,
		promptFolderSelectOnUpload,
	} = useAttachmentsContext();

	const { itemId, loading, searchValue } = useSelector((state) => state.attachmentsTable);

	const [pendingFiles, setPendingFiles] = useState(null);
	const [folderDialogOpen, setFolderDialogOpen] = useState(false);

	const rootFolderId = useMemo(
		() => getRootFolderId({ deal, client, relationshipCompany }),
		[deal, client, relationshipCompany],
	);

	const {
		currentFile,
		dialogOpen,
		uploadLoading,
		processFilesForUpload,
		handleCreateCopy,
		handleNextFile,
	} = useSharepointUploadConflicts({
		driveId: clientFolderDriveId,
		itemId,
		handleUploadFiles,
	});

	const isLoading = uploadLoading || loading;
	const disableUpload = !itemId || !canCreate || searchValue.length > 0;

	const hasEntity = Object.keys(client ?? {}).length > 0 || deal || relationshipCompany;

	const folderFormInitialValues = useMemo(
		() => ({
			deal: deal ?? null,
			client: client ?? null,
			relationshipCompany: relationshipCompany ?? null,
			selectedFolder: rootFolderId ?? '',
			selectedDrive: null,
			folderPath: '',
			driveId: clientFolderDriveId,
		}),
		[deal, client, relationshipCompany, rootFolderId, clientFolderDriveId],
	);

	const handleUpload = useCallback(
		async (newFiles) => {
			if (disableUpload) {
				return;
			}

			if (promptFolderSelectOnUpload) {
				setPendingFiles(newFiles);
				setFolderDialogOpen(true);
				return;
			}

			await processFilesForUpload(newFiles);
		},
		[disableUpload, promptFolderSelectOnUpload, processFilesForUpload],
	);

	const closeFolderDialog = useCallback(() => {
		setFolderDialogOpen(false);
		setPendingFiles(null);
	}, []);

	const uploadUi = (
		<>
			<Box width='100%' pb={'1em'}>
				{hasEntity && !disableUpload && (
					<FileUpload files={[]} setFiles={handleUpload} loading={isLoading} />
				)}
			</Box>
			<FileExistsDialog
				open={dialogOpen}
				fileName={currentFile?.name}
				onClose={handleNextFile}
				onCreateCopy={handleCreateCopy}
			/>
		</>
	);

	if (!promptFolderSelectOnUpload) {
		return uploadUi;
	}

	return (
		<Formik enableReinitialize initialValues={folderFormInitialValues}>
			{({ resetForm }) => (
				<>
					{uploadUi}
					<FolderSelectDialog
						initialItemId={rootFolderId}
						open={folderDialogOpen}
						handleClose={() => {
							closeFolderDialog();
							resetForm();
						}}
						onConfirm={async ({ selectedFolder }) => {
							if (!pendingFiles?.length || !selectedFolder) {
								return;
							}

							const filesToUpload = pendingFiles;
							closeFolderDialog();
							resetForm();

							await processFilesForUpload(filesToUpload, selectedFolder);
						}}
					/>
				</>
			)}
		</Formik>
	);
};